import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAtom } from "jotai";
import { CustomCard, CardBody } from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import NextMeetingCard from "./components/NextMeetingCard";
import PreviousMeetingCard from "./components/PreviousMeetingCard";
import LateCancellationModal from "./components/LateCancellationModal";
import SuccessfullyCancelledModal from "./components/SuccessfullyCancelledModal";
import ConfirmCancellationModal from "./components/ConfirmCancellationModal";
import Chat from "../../components/Chat/Chat";
import NoCoachCard from "./components/NoCoachCard";
import Api from "../../services/Api";
import { ClientLicenseInterface } from "../../interfaces/common";
import { CoachProfile, Meeting } from "./interfaces";
import { CameraIcon } from "../../components/Icons/VideoIcons";
import {
  addMinutes,
  formatDateYear,
  minutesToTime,
} from "../../helpers/dateFunctions";
import AccountIcon from "../../components/Icons/AccountIcon";
import ChatIcon from "../../components/Icons/ChatIcon";
import { getLanguage } from "../../lang/helpers";
import { CalendarWeek as CalendarIcon } from "flowbite-react-icons/outline";
import { unreadChatsAtom } from "../../jotai_state";

interface CoachData {
  name: string;
  id: string;
  cancelPolicy: number;
  picture: string;
  profile: CoachProfile;
}

const VideocoachPage: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  const [nextMeetings, setNextMeetings] = useState<Meeting[]>([]);
  const [previousMeetings, setPreviousMeetings] = useState<Meeting[]>([]);
  const [activeMeeting, setActiveMeeting] = useState<Meeting | undefined>(undefined);
  const [isLateCancellationModalOpen, setIsLateCancellationModalOpen] = useState(false);
  const [isSuccessfullyCancelledModalOpen, setIsSuccessfullyCancelledModalOpen] = useState(false);
  const [isConfirmCancellationModalOpen, setIsConfirmCancellationModalOpen] = useState(false);
  const [coachData, setCoachData] = useState<CoachData | null>(null);
  const [license, setLicense] = useState<ClientLicenseInterface | null>(null);
  const [showChat, setShowChat] = useState<boolean>(false);
  const [unreadChats, setUnreadChats] = useAtom(unreadChatsAtom);
  const [loaded, setLoaded] = useState(false);
  const now = new Date();

  useEffect(() => {
    getStartData();
  }, []);

  const getStartData = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let url = "client/coach_start";
    if (timezone) {
      url += "?tz=" + timezone;
    }
    api.get(url).then((response) => {
      if (response.json.error === "No license") {
        setLoaded(true);
        return;
      }
      if (response.json.license) {
        setLicense(response.json.license);
      }
      if (response.json.coach) {
        setCoachData(response.json.coach);
        if (response.json.meetings) {
          const prevM: Array<Meeting> = [];
          const nextM: Array<Meeting> = [];
          for (let m of response.json.meetings as Array<Meeting>) {
            m.startDate = new Date(m.start);
            if (addMinutes(m.startDate, m.length) > now) {
              nextM.push(m);
            } else {
              prevM.push(m);
            }
          }
          setNextMeetings(nextM);
          setPreviousMeetings(prevM);
        }
      }
      setLoaded(true);
    });
  };

  const openCancelModal = (meeting: Meeting) => {
    if (!coachData) return; // Ensure coachData is available

    const latestCancellationDateTime = new Date(meeting.startDate);
    latestCancellationDateTime.setHours(
      latestCancellationDateTime.getHours() - coachData.cancelPolicy
    );

    setActiveMeeting(meeting);

    if (now < latestCancellationDateTime) {
      setIsConfirmCancellationModalOpen(true);
    } else {
      setIsLateCancellationModalOpen(true);
    }
  };

  const renderMeetingInfo = () => {
    if (nextMeetings.length > 0) {
      return (
        <NextMeetingCard
          meetingData={nextMeetings}
          openCancelModal={openCancelModal}
          license={license}
        />
      );
    } else {
      const remainingMeetings =
        license && license.remainingMeetings ? license.remainingMeetings : 0;
      return (
        <CustomCard className="overflow-visible">
          <CardBody className="p-4 flex flex-col gap-4 overflow-visible">
            <div className="flex flex-col py-4 gap-2 justify-center align-middle items-center">
              <CalendarIcon className="w-8 h-8" aria-hidden="true" />
              <p className="z-body1 text-white whitespace-pre-line text-center">
                {remainingMeetings > 0
                  ? t("no-booked-meetings")
                  : t("you-have-had-all-meetings")}
              </p>
            </div>

            {remainingMeetings > 0 && (
              <CustomButton
                buttonType="blueBase"
                onClick={() => navigate("/videocoach/book-meeting")}
              >
                {t("book-meeting")}
              </CustomButton>
            )}
          </CardBody>
        </CustomCard>
      );
    }
  };

  const cancelMeeting = (meeting: Meeting) => {
    api.get("client/meeting_cancel/" + meeting.id).then((response) => {
      getStartData();
      setIsLateCancellationModalOpen(false);
      setIsConfirmCancellationModalOpen(false);
      setIsSuccessfullyCancelledModalOpen(true);
    });
  };

  if (!loaded) {
    return <div className="mt-4">Loading...</div>;
  }
  if (!license) {
    return (
      <div className="main-content">
        <div className="flex flex-col h-full flex-grow min-h-0 gap-6 mt-6">
          <CameraIcon alt="camera" className="w-8 h-8 mx-auto" />
          <div className="flex flex-col gap-2 text-center">
          <p>
            {lang === "sv"
              ? "Videocoaching är inte tillgängligt på ditt konto.Om du tror att detta är ett misstag, vänligen kontakta oss på"
              : "Video coaching isn’t available on your account.If you believe this is a mistake, please contact us at"}
          </p>  
            <p>
              <a
                href="mailto:support@zebrain.se"
                style={{ color: "#98AAF9", textDecoration: "underline" }}
              >
                support@zebrain.se
              </a>{" "}
              {lang === "sv"
                ? " - vi finns här för att hjälpa dig"
                : " - we’re here to help."}
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (!coachData) {
    return (
      <div className="main-content">
        <div className="flex flex-col h-full flex-grow min-h-0 gap-6 mt-6">
          <h1 className="z-heading1 text-white">
            {t("video-coach-nav-title")}
          </h1>
          <div className="flex flex-col gap-2">
            <div className="z-title1 text-white">{t("my-coach")}</div>
            <NoCoachCard />
          </div>
        </div>
      </div>
    );
  }

  return (
    // <div className="flex flex-col h-full flex-grow min-h-0 mt-6">
    <div className="main-content">
      <h1 className="mt-6 z-heading1 text-white">
        {t("video-coach-nav-title")}
      </h1>

      {/* Next meeting */}
      <div className="mt-6 flex flex-col gap-2">
        <div className="z-title1 text-white">
          {t("booked-meetings")}
        </div>
        {renderMeetingInfo()}
      </div>

      <div className="mt-6 z-title1 text-white">
        {lang === "sv" ? "Coachingperiod" : "Coaching period"}
      </div>

      <div className="mt-2 bg-indigo-900 rounded-md p-4">
        <div className="z-title1 text-white">
          {lang === "sv" ? "Periodens längd" : "Period duration"}
        </div>
        <div className="mt-2 flex justify-between text-gray-400">
          <p>{lang === "sv" ? "Startdatum" : "Start date"}</p>
          <p>{formatDateYear(license.start)}</p>
        </div>
        <div className="mt-2 flex justify-between text-gray-400">
          <p>{lang === "sv" ? "Slutdatum" : "End date"}</p>
          <p>{formatDateYear(license.end)}</p>
        </div>

        <hr className="mt-4 border-indigo-700" />

        <div className="mt-4 z-title1 text-white">
          {lang === "sv" ? "Sessionsöversikt" : "Session overview"}
        </div>
        <div className="mt-2 flex justify-between text-gray-400">
          <p>{lang === "sv" ? "Bokade" : "Booked"}</p>
          <p>
            {license.consumedMeetings - license.hadMeetings} &nbsp; (
            {minutesToTime(
              license.consumedMeetingTime - license.hadMeetingTime
            )}{" "}
            h)
          </p>
        </div>
        <div className="mt-2 flex justify-between text-gray-400">
          <p>{lang === "sv" ? "Genomförda" : "Completed"}</p>
          <p>
            {license.hadMeetings} ({minutesToTime(license.hadMeetingTime)} h)
          </p>
        </div>
        <div className="mt-2 flex justify-between text-gray-400">
          <p>{lang === "sv" ? "Återstående" : "Remaining"}</p>
          <p>
            {license.remainingMeetings} (
            {minutesToTime(license.remainingCredits)} h)
          </p>
        </div>
      </div>

      {/* Previous meetings */}
      {previousMeetings.length > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          <div className="z-title1 text-white">
            {t("previous-meetings")}
          </div>
          <PreviousMeetingCard meetingData={previousMeetings} />
        </div>
      )}

      {/* My Coach */}
      <div className="mt-6 flex flex-col gap-2">
        <div
          className="z-title1 text-white"
          onClick={() => setShowChat(!showChat)}
        >
          {t("my-coach")}
        </div>
        <div className="bg-indigo-900 rounded p-4">
          <div className="flex items-center">
            {coachData.picture ? (
              <img alt="pic" className="picture-lg" src={coachData.picture} />
            ) : (
              <AccountIcon alt="pic" className="picture-lg" />
            )}
            <div className="ml-4 z-heading2">{coachData.name}</div>
          </div>
          <div className="mt-6">
            <CustomButton
              leftIcon={<ChatIcon variant="white" alt="chat" className="w-4 h-4" />}
              buttonType="outlineWhiteBase"
              className="relative"
              onClick={() => navigate("/videocoach/chat/" + coachData.id)}
            >
              Chat
              {unreadChats > 0 && (
                <div
                  className="inline h-2 w-2 rounded-full bg-cherry-500 absolute"
                  style={{ top: "19px", right: "calc(50% - 50px)" }}
                />
              )}
            </CustomButton>
          </div>
          <div className="mt-2">
            <CustomButton
              buttonType="outlineWhiteBase"
              onClick={() => navigate("/videocoach/my-coach")}
            >
              {lang === "sv" ? "Läs mer" : "Read more"}
            </CustomButton>
          </div>
        </div>
      </div>

      {showChat && <Chat isCoach={false} coachId={coachData.id} />}

      <LateCancellationModal
        isOpen={isLateCancellationModalOpen && activeMeeting != undefined}
        onClose={() => setIsLateCancellationModalOpen(false)}
        coachName={coachData.name}
        cancelMeeting={() => activeMeeting && cancelMeeting(activeMeeting)}
      />
      <SuccessfullyCancelledModal
        isOpen={isSuccessfullyCancelledModalOpen}
        onClose={() => setIsSuccessfullyCancelledModalOpen(false)}
        coachName={coachData.name}
      />
      <ConfirmCancellationModal
        isOpen={isConfirmCancellationModalOpen && activeMeeting !== null}
        onClose={() => setIsConfirmCancellationModalOpen(false)}
        meeting={activeMeeting}
        coachName={coachData.name}
        cancelMeeting={() => activeMeeting && cancelMeeting(activeMeeting)}
      />
    </div>
  );
};

export default VideocoachPage;
